import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  Dialog,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";

import useApi from "../../hooks/useApi";
import { useDebounce } from "../../hooks/useDebounce";
import { buildSpacesImageUrl } from "../../utils/spacesImageUrl";
import Dialog_YesNo from "./Dialog_YesNo";

const SurveyImageCapture = ({
  value,
  onChange,
  imageType = "standard",
  minImages = 0,
  maxImages = 10,
  resolution = "high",
  disabled = false,
}) => {
  const theme = useTheme();
  const { uploadFile, deleteFile } = useApi();

  const [images, setImages] = useState([]);
  const [beforeImages, setBeforeImages] = useState([]);
  const [afterImages, setAfterImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    index: null,
    type: null,
  });
  const [commentDraftVersion, setCommentDraftVersion] = useState(0);
  const debouncedCommentDraftVersion = useDebounce(commentDraftVersion, 450);

  const imagesRef = useRef(images);
  const beforeImagesRef = useRef(beforeImages);
  const afterImagesRef = useRef(afterImages);
  const onChangeRef = useRef(onChange);
  const hasPendingCommentSaveRef = useRef(false);

  onChangeRef.current = onChange;

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect(() => {
    beforeImagesRef.current = beforeImages;
  }, [beforeImages]);

  useEffect(() => {
    afterImagesRef.current = afterImages;
  }, [afterImages]);

  // Parse and normalize images from value
  useEffect(() => {
    try {
      const parsed = typeof value === "string" ? JSON.parse(value) : value;

      if (imageType === "beforeAfter") {
        // Handle before/after structure
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          setBeforeImages(normalizeImageArray(parsed.before || []));
          setAfterImages(normalizeImageArray(parsed.after || []));
        } else {
          setBeforeImages([]);
          setAfterImages([]);
        }
      } else {
        // Standard mode
        const arr = Array.isArray(parsed) ? parsed : [];
        setImages(normalizeImageArray(arr));
      }
    } catch (e) {
      console.error("Error parsing image value:", e);
      if (imageType === "beforeAfter") {
        setBeforeImages([]);
        setAfterImages([]);
      } else {
        setImages([]);
      }
    }
  }, [value, imageType]);

  // Convert array of strings or objects to normalized format
  const normalizeImageArray = (arr) => {
    return arr.map((item) => {
      if (typeof item === "string") {
        return {
          fileName: item,
          url: buildSpacesImageUrl(item, { defaultFolder: "survey-images" }),
          comment: "",
        };
      }
      return {
        fileName: item.fileName,
        url: buildSpacesImageUrl(item.fileName, {
          defaultFolder: "survey-images",
        }),
        comment: item.comment || "",
      };
    });
  };

  const handleUpload = async (type = null) => {
    if (disabled) return;

    const currentImages =
      imageType === "beforeAfter"
        ? type === "before"
          ? beforeImages
          : afterImages
        : images;

    if (currentImages.length >= maxImages) {
      alert(`You can only upload up to ${maxImages} images.`);
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = maxImages - currentImages.length > 1;

    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      setUploading(true);
      try {
        const uploadedImages = [];

        for (const file of files) {
          if (currentImages.length + uploadedImages.length >= maxImages) break;

          // Generate unique filename
          const ext = file.name.split(".").pop();
          const key = `${crypto.randomUUID()}.${ext}`;

          // Upload to Spaces
          const result = await uploadFile(file, {
            folder: "survey-images",
            key,
          });

          if (result?.url) {
            uploadedImages.push({
              fileName: result?.fileName || key,
              url: result.url,
              comment: "",
            });
          }
        }

        if (uploadedImages.length > 0) {
          const newImages = [...currentImages, ...uploadedImages];

          if (imageType === "beforeAfter") {
            if (type === "before") {
              setBeforeImages(newImages);
              saveBeforeAfter(newImages, afterImages);
            } else {
              setAfterImages(newImages);
              saveBeforeAfter(beforeImages, newImages);
            }
          } else {
            setImages(newImages);
            saveStandard(newImages);
          }
        }
      } catch (err) {
        console.error("Upload error:", err);
        alert("Failed to upload images. Please try again.");
      } finally {
        setUploading(false);
      }
    };

    input.click();
  };

  const handleDelete = (index, type = null) => {
    if (disabled) return;
    setDeleteDialog({ open: true, index, type });
  };

  const confirmDelete = async () => {
    const { index, type } = deleteDialog;
    setDeleteDialog({ open: false, index: null, type: null });

    const currentImages =
      imageType === "beforeAfter"
        ? type === "before"
          ? beforeImages
          : afterImages
        : images;

    const imageToDelete = currentImages[index];

    try {
      // Delete from Spaces
      if (imageToDelete.fileName) {
        await deleteFile("survey-images", imageToDelete.fileName);
      }

      const newImages = currentImages.filter((_, i) => i !== index);

      if (imageType === "beforeAfter") {
        if (type === "before") {
          setBeforeImages(newImages);
          saveBeforeAfter(newImages, afterImages);
        } else {
          setAfterImages(newImages);
          saveBeforeAfter(beforeImages, newImages);
        }
      } else {
        setImages(newImages);
        saveStandard(newImages);
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete image from storage.");
    }
  };

  const handleCommentChange = (index, comment, type = null) => {
    if (disabled) return;

    if (imageType === "beforeAfter") {
      const currentImages = type === "before" ? beforeImages : afterImages;
      const newImages = [...currentImages];
      newImages[index] = { ...newImages[index], comment };

      if (type === "before") {
        setBeforeImages(newImages);
      } else {
        setAfterImages(newImages);
      }
    } else {
      const newImages = [...images];
      newImages[index] = { ...newImages[index], comment };
      setImages(newImages);
    }

    hasPendingCommentSaveRef.current = true;
    setCommentDraftVersion((v) => v + 1);
  };

  const saveStandard = (imageList) => {
    const serialized = JSON.stringify(
      imageList.map((img) => ({
        fileName: img.fileName,
        comment: img.comment,
      })),
    );
    onChangeRef.current?.(serialized);
  };

  const saveBeforeAfter = (beforeList, afterList) => {
    const serialized = JSON.stringify({
      before: beforeList.map((img) => ({
        fileName: img.fileName,
        comment: img.comment,
      })),
      after: afterList.map((img) => ({
        fileName: img.fileName,
        comment: img.comment,
      })),
    });
    onChangeRef.current?.(serialized);
  };

  const commitPendingCommentSave = () => {
    if (!hasPendingCommentSaveRef.current) return;

    if (imageType === "beforeAfter") {
      saveBeforeAfter(beforeImagesRef.current, afterImagesRef.current);
    } else {
      saveStandard(imagesRef.current);
    }

    hasPendingCommentSaveRef.current = false;
  };

  useEffect(() => {
    if (disabled) return;
    if (debouncedCommentDraftVersion === 0) return;

    commitPendingCommentSave();
  }, [debouncedCommentDraftVersion, disabled, imageType]);

  useEffect(() => {
    return () => {
      // Mobile parity: flush pending comment edits when component unmounts.
      commitPendingCommentSave();
    };
  }, []);

  const renderImageGroup = (imageList, type, label) => (
    <Box sx={{ marginBottom: 3, width: "100%" }}>
      <Typography
        variant="h6"
        sx={{ marginBottom: 2, color: theme.palette.primary.main }}
      >
        {label}
      </Typography>

      {!disabled && (
        <Button
          variant="contained"
          startIcon={<AddPhotoAlternateIcon />}
          onClick={() => handleUpload(type)}
          disabled={uploading || imageList.length >= maxImages}
          sx={{ marginBottom: 2 }}
        >
          Upload {label} Image
        </Button>
      )}

      {imageList.length === 0 ? (
        <Typography sx={{ marginTop: 2, fontStyle: "italic", color: "#666" }}>
          No {label.toLowerCase()} images yet
        </Typography>
      ) : (
        <Stack spacing={2}>
          {imageList.map((img, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                padding: 2,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                border: "1px solid #ddd",
              }}
            >
              <Box
                sx={{
                  width: 150,
                  height: 150,
                  flexShrink: 0,
                  cursor: "pointer",
                }}
                onClick={() => setSelectedImage(img.url)}
              >
                <img
                  src={img.url}
                  alt={`${label} ${idx + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                  onError={(e) => {
                    console.error(`Failed to load image: ${img.fileName}`);
                    e.target.style.display = "none";
                  }}
                />
              </Box>

              <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <TextField
                  placeholder="Add a comment..."
                  value={img.comment || ""}
                  onChange={(e) =>
                    handleCommentChange(idx, e.target.value, type)
                  }
                  disabled={disabled}
                  multiline
                  rows={3}
                  fullWidth
                  sx={{
                    marginBottom: 1,
                    "& .MuiInputBase-root": {
                      padding: "4px 8px !important",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "10px",
                      padding: "4px !important",
                    },
                  }}
                />

                {!disabled && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(idx, type)}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Delete
                  </Button>
                )}
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      <Typography
        variant="caption"
        sx={{ marginTop: 1, display: "block", color: "#666" }}
      >
        {imageList.length}/{maxImages} images (min {minImages} required)
      </Typography>
    </Box>
  );

  if (imageType === "beforeAfter") {
    return (
      <Box sx={{ padding: 2 }}>
        {renderImageGroup(beforeImages, "before", "BEFORE")}
        {renderImageGroup(afterImages, "after", "AFTER")}

        <Typography
          variant="caption"
          sx={{ marginTop: 2, display: "block" }}
        >
          Resolution: {resolution}
        </Typography>

        <Dialog
          open={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          maxWidth="lg"
          fullWidth
        >
          <IconButton
            onClick={() => setSelectedImage(null)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
              backgroundColor: "rgba(0,0,0,0.5)",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Full size"
              style={{
                width: "100%",
                height: "auto",
                display: "block",
              }}
            />
          )}
        </Dialog>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 2 }}>
      {!disabled && (
        <Button
          variant="contained"
          startIcon={<AddPhotoAlternateIcon />}
          onClick={() => handleUpload()}
          disabled={uploading || images.length >= maxImages}
          sx={{ marginBottom: 2 }}
        >
          Upload Image
        </Button>
      )}

      {images.length === 0 ? (
        <Typography sx={{ marginTop: 2, color: "#666" }}>
          No images captured yet
        </Typography>
      ) : (
        <Stack spacing={2}>
          {images.map((img, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                padding: 2,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                border: "1px solid #ddd",
              }}
            >
              <Box
                sx={{
                  width: 150,
                  height: 150,
                  flexShrink: 0,
                  cursor: "pointer",
                }}
                onClick={() => setSelectedImage(img.url)}
              >
                <img
                  src={img.url}
                  alt={`Image ${idx + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                  onError={(e) => {
                    console.error(`Failed to load image: ${img.fileName}`);
                    e.target.style.display = "none";
                  }}
                />
              </Box>

              <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <TextField
                  placeholder="Add a comment..."
                  value={img.comment || ""}
                  onChange={(e) => handleCommentChange(idx, e.target.value)}
                  disabled={disabled}
                  multiline
                  rows={3}
                  fullWidth
                  sx={{
                    marginBottom: 1,
                    "& .MuiInputBase-root": {
                      padding: "4px 8px !important",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "10px",
                      padding: "4px !important",
                    },
                  }}
                />

                {!disabled && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(idx)}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Delete
                  </Button>
                )}
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      <Typography
        variant="caption"
        sx={{ marginTop: 2, display: "block", color: "#666" }}
      >
        {images.length}/{maxImages} images (min {minImages} required) —
        Resolution: {resolution}
      </Typography>

      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="lg"
        fullWidth
      >
        <IconButton
          onClick={() => setSelectedImage(null)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            backgroundColor: "rgba(0,0,0,0.5)",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
          }}
        >
          <CloseIcon />
        </IconButton>
        {selectedImage && (
          <img
            src={selectedImage}
            alt="Full size"
            style={{
              width: "100%",
              height: "auto",
              display: "block",
            }}
          />
        )}
      </Dialog>

      <Dialog_YesNo
        open={deleteDialog.open}
        onClose={() =>
          setDeleteDialog({ open: false, index: null, type: null })
        }
        title="Delete Image"
        message="Are you sure you want to delete this image? This cannot be undone!"
        onYes={confirmDelete}
        onNo={() => setDeleteDialog({ open: false, index: null, type: null })}
      />
    </Box>
  );
};

export default SurveyImageCapture;
